// src/components/Dashboard/Dashboard.jsx
import React, { useState, useEffect, useContext, useMemo, Suspense, lazy } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageContext } from '../../App';
import WorkspacePreferences from '../WorkspacePreferences';
import {
  updateOperatorDebtPayment,
  deleteOperatorDebt,
  auditOperatorDebts,
  cleanupPrematureOperatorDebts,
  updatePendingDebtsToCurrentFee,
  OPERATOR_FEE,
  getGlobalSettings
} from '../../services/claimService';
import QuickCreate from './QuickCreate';
import OverviewCards from './OverviewCards';
import AdjusterHome from './AdjusterHome';
import AdjusterShell, { adjusterTabs, homeTabFor } from './AdjusterShell';
import useAnalytics from '../../hooks/useAnalytics';
import TeamWorkspace from './TeamWorkspace';
import ClaimsDirectory from './ClaimsDirectory';
import './ClaimsDirectory.css';
import UpcomingMeetingsWidget from '../UpcomingMeetingsWidget';
import ClaimStatusHero from '../ClaimStatusHero';
import ClaimAgendaPage from '../ClaimAgendaPage';
import InitialPasswordChange from '../InitialPasswordChange';
import { subscribeWorkspace } from '../../services/workspaceService';
import { subscribeAllClaimNotes } from '../../services/claimNoteService';
import { migrateLegacyAdjusterData } from '../../services/adjusterService';
import './Dashboard.css';
import '../ClaimStatus.css';

// Pantallas de administración poco frecuentes y el gráfico (recharts): se
// descargan solo al abrirlas, no con el panel inicial.
const OperatorDebtsPanel = lazy(() => import('./OperatorDebtsPanel'));
const ClientInvoicesPanel = lazy(() => import('./ClientInvoicesPanel'));
const PaymentCutsPanel = lazy(() => import('./PaymentCutsPanel'));
const Companies = lazy(() => import('./Companies'));
const UsersManager = lazy(() => import('./UsersManager'));
const DocusignInbox = lazy(() => import('./DocusignInbox'));
const CompanyOverview = lazy(() => import('./CompanyOverview'));
const ActivityInbox = lazy(() => import('./ActivityInbox'));
const StatusCatalog = lazy(() => import('./StatusCatalog'));
const OASync = lazy(() => import('./OASync'));
const FlowMigration = lazy(() => import('./FlowMigration'));
const CsrEnrollment = lazy(() => import('./CsrEnrollment'));
const MonthlyCsrChart = lazy(() => import('./MonthlyCsrChart'));
const tabFallback = <div className="cd-loading" role="status"><div className="spinner"></div></div>;

// Secciones del menú de administración → vistas (activeTab) que agrupan.
const ADMIN_SECTIONS = [
  { id: 'home', tabs: ['analytics'], icon: 'fa-solid fa-chart-line', es: 'Resumen', en: 'Overview' },
  { id: 'claims', tabs: ['claimstatus'], icon: 'fa-solid fa-file-shield', es: 'Reclamos', en: 'Claims' },
  { id: 'commissions', tabs: ['commissions'], icon: 'fa-solid fa-coins', es: 'Comisiones por cheque', en: 'Check commissions' },
  { id: 'create', tabs: ['create'], icon: 'fa-solid fa-plus', es: 'Nuevo reclamo', en: 'New claim' },
  { id: 'requests', tabs: ['tickets', 'activity'], icon: 'fa-solid fa-note-sticky', es: 'Casos y notas', en: 'Cases & notes' },
  { id: 'schedule', tabs: ['schedule'], icon: 'fa-regular fa-calendar', es: 'Agenda', en: 'Schedule' },
  { id: 'payments', tabs: ['clientinvoices', 'cuts', 'debts'], icon: 'fa-solid fa-coins', es: 'Facturas y deuda', en: 'Invoices & debt' },
  { id: 'settings', tabs: ['companies', 'statuses'], icon: 'fa-solid fa-building', es: 'Empresas y estados', en: 'Companies & statuses' },
  { id: 'users', tabs: ['users'], icon: 'fa-solid fa-users', es: 'Usuarios', en: 'Users' },
  // Sync OA y Migración son herramientas temporales (ver OASync.jsx / FlowMigration.jsx):
  // van aparte de la gestión diaria de empresas y ajustadores.
  { id: 'tools', tabs: ['oasync', 'migration', 'csrenroll'], icon: 'fa-solid fa-screwdriver-wrench', es: 'Herramientas', en: 'Admin tools' }
];
// 'docusign' = bandeja de avisos de firma del CSR Assistant (el admin los ve en Casos y notas).
// 'home' y 'account' son solo del panel sin admin (AdjusterShell).
const ALL_TABS = [...ADMIN_SECTIONS.flatMap(section => section.tabs), 'docusign', 'overview', 'home', 'account'];
const NON_ADMIN_TABS = ['home', 'account'];
const SUB_TAB_LABELS = {
  tickets: ['Casos', 'Cases'],
  activity: ['Actividad', 'Activity'],
  clientinvoices: ['Facturas a clientes', 'Client invoices'],
  cuts: ['Cortes de pagos', 'Payment cuts'],
  debts: ['Deuda de operador', 'Operator debt'],
  companies: ['Empresas y ajustadores', 'Companies & adjusters'],
  statuses: ['Estados y colores', 'Statuses & colors'],
  oasync: ['Sync Organized Adjuster', 'Organized Adjuster sync'],
  migration: ['Migración de pasos', 'Step migration'],
  csrenroll: ['Reclamos de CSR (*)', 'CSR claims (*)']
};

const Dashboard = () => {
  const { language } = useContext(LanguageContext);
  const {
    currentUser, logout, isAdmin, can, userRoleKey,
    userCompany, userAdjusterName, userCanViewAllClaims, mustChangePassword
  } = useAuth();
  // Panel único por rol (fusión 2026-09-21, ver conversación) — Dashboard.jsx
  // dejó de ser admin-only, así que cualquier suscripción sin where() sobre
  // colecciones admin-only debe apagarse para el ajustador o Firestore la
  // rechaza al instante (ver useAnalytics.js y el efecto de "Casos y notas"
  // más abajo).
  const { stats, claims, operatorDebts, loading, error: analyticsError, refresh: refreshData,
    clientInvoices, clientInvoicesError, clientInvoicesLoading } = useAnalytics(isAdmin);
  // Empresas y sus ajustadores, para facturar a clientes.
  const adjustersByCompany = useMemo(() => {
    const map = {};
    claims.forEach(claim => {
      if (!claim.companyName) return;
      map[claim.companyName] = map[claim.companyName] || new Set();
      if (claim.adjusterName) map[claim.companyName].add(claim.adjusterName);
    });
    return Object.fromEntries(Object.entries(map).map(([company, names]) => [company, [...names].sort()]));
  }, [claims]);
  // La sección activa vive en la URL (?tab=): recargar, volver desde una ficha
  // o usar Atrás la recupera, y los parámetros de cada pantalla (filtros de
  // Reclamos, por ejemplo) se conservan al cambiar de sección.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // Pestaña de entrada: admin → Reclamos; P.A Admin → Resumen; CSR Assistant
  // → Reclamos; P.A y asistente → Inicio (AdjusterShell.homeTabFor). Sin
  // admin solo valen sus pestañas, "Nuevo reclamo" y "Mi cuenta".
  const defaultTab = isAdmin ? 'claimstatus' : homeTabFor(userRoleKey);
  const allowedTab = tab => (isAdmin
    ? ALL_TABS.includes(tab) && !NON_ADMIN_TABS.includes(tab)
    : ['create', 'account', ...adjusterTabs(can, userRoleKey).map(item => item.id)].includes(tab));
  const activeTab = allowedTab(tabParam) ? tabParam : defaultTab;
  const setActiveTab = tab => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (tab === defaultTab) next.delete('tab'); else next.set('tab', tab);
    return next;
  });
  const [duplicateSource, setDuplicateSource] = useState(null);
  // Menú de secciones en móvil (panel inferior).
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = event => { if (event.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  // Contenido de la pestaña "Claim Status" — portado de ClaimStatusGate.jsx
  // al fusionar el panel en uno solo por rol: admin ve todas las empresas
  // (props null), ajustador ve solo la suya, igual que antes en /claim-status.
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  useEffect(() => {
    if (isAdmin) return; // el header de admin no usa ClaimStatusHero, no hace falta el logo
    let active = true;
    setCompanyLogo(null);
    setCompanyName(null);
    getGlobalSettings().then(settings => {
      if (!active) return;
      setCompanyName(userCompany);
      setCompanyLogo(settings?.companyLogos?.[userCompany] || null);
    }).catch(() => {});
    return () => { active = false; };
  }, [isAdmin, userCompany]);
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  // Respaldo para admin en "Casos y notas": notas de reclamos escritas por
  // el ajustador (o el admin) en ClaimNotes.jsx — pedido explícito
  // 2026-09-20, ver subscribeAllClaimNotes en claimNoteService.js.
  const [claimNotes, setClaimNotes] = useState([]);
  const [workspaceError, setWorkspaceError] = useState(false);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [workspaceAttempt, setWorkspaceAttempt] = useState(0);

  useEffect(() => {
    // notifications/claimNotes sin where() — solo admin puede suscribirse
    // sin filtro (ver firestore.rules); el ajustador no ve "Casos y notas"
    // ni "Actividad" en el sidebar, así que no hace falta ni intentarlo.
    if (!isAdmin) { setWorkspaceLoading(false); return; }
    setWorkspaceError(false);
    setWorkspaceLoading(true);
    let loaded = 0;
    const receive = setter => data => { setter(data); loaded |= setter === setTickets ? 1 : 2; if (loaded === 3) setWorkspaceLoading(false); };
    const failed = () => { setWorkspaceError(true); setWorkspaceLoading(false); };
    const stopTickets = subscribeWorkspace('ticket', receive(setTickets), failed);
    const stopEvents = subscribeWorkspace('activity', receive(setEvents), failed);
    const stopClaimNotes = subscribeAllClaimNotes(setClaimNotes, () => {});
    return () => { stopTickets(); stopEvents(); stopClaimNotes(); };
  }, [workspaceAttempt, isAdmin]);

  useEffect(() => {
    // Corre (y se espera) antes que cualquier pestaña necesite `adjusters`
    // (Crear Rápido, Mis Reclamos) — si se dejara solo en la pestaña
    // "Ajustadores", un admin que abra Crear Rápido primero vería el
    // selector vacío hasta visitar esa pestaña al menos una vez.
    // Idempotente: en cargas siguientes es solo una lectura de un flag, no
    // vuelve a migrar nada.
    const init = async () => {
      try {
        // Fuerza a que el SDK de Auth termine de adjuntar el ID token antes
        // de la primera consulta a Firestore. En una carga con sesión
        // persistida (IndexedDB), currentUser ya existe cuando este efecto
        // corre, pero el token todavía puede no estar listo del lado de
        // Firestore, lo que producía un permission-denied intermitente acá
        // (la primera lectura del componente) que no se veía en loadData()
        // por disparar unos milisegundos después.
        if (currentUser) await currentUser.getIdToken();
        await migrateLegacyAdjusterData();
      } catch (err) {
        console.error('Error migrating legacy adjuster data:', err);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const handleClaimCreated = () => {
    setDuplicateSource(null);
    setActiveTab('claimstatus');
  };


  // Sin try/catch propio a propósito: OperatorDebtsPanel es quien llama a
  // estos handlers y necesita que el error se propague para mostrar su
  // propio toast de error. Atraparlo acá lo dejaba siempre mostrando
  // "pagado con éxito" aunque la escritura a Firestore fallara.
  const handleDebtPaid = async (debtId) => {
    await updateOperatorDebtPayment(debtId, true);
  };

  const handleDeleteDebt = async (debtId) => {
    await deleteOperatorDebt(debtId);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{language === 'es' ? 'Cargando...' : 'Loading...'}</p>
      </div>
    );
  }

  // Mismo gate que antes vivía en ClaimStatusGate.jsx — el ajustador con
  // acceso recién creado/reseteado debe cambiar su contraseña antes de ver
  // nada del panel único.
  if (!isAdmin && mustChangePassword) {
    return <InitialPasswordChange />;
  }

  const claimStatusContent = (
    <>
      {/* El resto de roles navega con AdjusterShell (pestañas y barra
          inferior) y ve sus citas en el Inicio. */}
      {isAdmin && (<>
        <div className="workspace-claims-heading">
          <h2>{language === 'es' ? 'Reclamos' : 'Claims'}</h2>
          <div className="cd-container cs-new-claim-bar">
            <Link to="/schedule" className="cd-btn cd-btn-invoice cs-new-claim-btn">
              <i className="fa-regular fa-calendar"></i> {language === 'es' ? 'Mi agenda' : 'My schedule'}
            </Link>
            <button type="button" className="cd-btn cd-btn-check cs-new-claim-btn" onClick={() => setActiveTab('create')}>
              <i className="fa-solid fa-plus"></i> {language === 'es' ? 'Nuevo reclamo' : 'New claim'}
            </button>
          </div>
        </div>
        <UpcomingMeetingsWidget />
      </>)}
      {/* Mismo ClaimsDirectory para los dos roles — admin sin locks ve TODAS
          las empresas; ajustador queda fijo a la suya, igual que antes en
          /claim-status. Panel único por rol, fusión 2026-09-21. */}
      {/* El CSR Assistant puede tener varias compañías: sin compañía fija,
          así ve el selector de empresa arriba (sus datos ya llegan solo de
          sus compañías, services/directoryData.js). */}
      <ClaimsDirectory
        view="claims"
        lockedCompany={isAdmin || userRoleKey === 'csr_assistant' ? null : userCompany}
        lockedAdjuster={!isAdmin && !userCanViewAllClaims ? userAdjusterName : null}
      />
    </>
  );

  // La insignia de "Casos y notas" cuenta actividad sin leer, así que si hay
  // alguna se entra por Actividad: abrir en Casos (que suele estar vacío)
  // contradecía el número. Solo cambia dónde se aterriza; nada se marca como
  // leído hasta que se pulsa el botón de Actividad.
  const hasUnreadActivity = events.some(item => !(item.readBy || []).includes(currentUser?.uid));
  const currentSection = ADMIN_SECTIONS.find(sec => sec.tabs.includes(activeTab));
  const unreadActivity = events.filter(item => !(item.readBy || []).includes(currentUser?.uid)).length;
  const firstTabOf = section => (section.id === 'requests' && hasUnreadActivity ? 'activity' : section.tabs[0]);

  return (
    <div className="dashboard-modern claim-status-page">
      {analyticsError && <div role="alert">{language === 'es' ? 'No se pudo actualizar Analytics. Recarga para reintentar; los datos pueden estar incompletos.' : 'Analytics could not update. Reload to retry; data may be incomplete.'}</div>}
      {isAdmin ? (
        <div className="dashboard-header-modern">
          <div className="dashboard-title">
            <h1>
              CSR <span>Claims Assistant</span>
            </h1>
            <p>{currentUser?.email}</p>
          </div>
          <div className="workspace-header-actions"><WorkspacePreferences />
          <button onClick={logout} className="logout-btn-glass">
            <i className="fa-solid fa-sign-out-alt"></i>
            {language === 'es' ? 'Salir' : 'Log out'}
          </button></div>
        </div>
      ) : null}

      {/* Roles sin admin: marco propio con pestañas arriba y, en móvil, una
          barra fija abajo (AdjusterShell). Abre en su Inicio (AdjusterHome).
          Overview, Avisos de firma y Usuarios siguen dependiendo del permiso
          y de firestore.rules, igual que antes. */}
      {!isAdmin ? (
        <AdjusterShell activeTab={activeTab} onNavigate={setActiveTab} companyLogo={companyLogo} companyName={companyName}>
          {activeTab === 'home' ? (
            <AdjusterHome lockedCompany={userCompany} lockedAdjuster={!userCanViewAllClaims ? userAdjusterName : null} onNavigate={setActiveTab} />
          ) : activeTab === 'activity' ? (
            <Suspense fallback={tabFallback}><ActivityInbox /></Suspense>
          ) : activeTab === 'overview' && can('viewOverview') ? (
            <Suspense fallback={tabFallback}><CompanyOverview onNavigate={setActiveTab} /></Suspense>
          ) : activeTab === 'docusign' && can('assignDocusign') ? (
            <Suspense fallback={tabFallback}><DocusignInbox /></Suspense>
          ) : activeTab === 'users' && can('manageUsers') ? (
            <Suspense fallback={tabFallback}><UsersManager /></Suspense>
          ) : activeTab === 'create' ? (
            <QuickCreate onClaimCreated={handleClaimCreated} duplicateFrom={duplicateSource} onDuplicateConsumed={() => setDuplicateSource(null)} />
          ) : activeTab === 'commissions' && can('viewCheckCommissions') ? (
            <ClaimsDirectory view="commissions" lockedCompany={userCompany} lockedAdjuster={!userCanViewAllClaims ? userAdjusterName : null} />
          ) : activeTab === 'cuts' && can('viewPaymentCuts') && userCompany ? (
            // Decisión del propietario (2026-10-24): el P.A Admin ve y descarga desde el
            // corte los invoices de su compañía (ya emitidos; abrirlos no escribe nada).
            <Suspense fallback={tabFallback}><PaymentCutsPanel lockedCompany={userCompany} canOpenInvoices /></Suspense>
          ) : activeTab === 'schedule' ? (
            <ClaimAgendaPage embedded />
          ) : activeTab === 'account' ? (
            <div className="as-account">
              {adjusterTabs(can, userRoleKey).some(tab => !tab.bottom) && (
                <nav className="as-more" aria-label={language === 'es' ? 'Más secciones' : 'More sections'}>
                  {adjusterTabs(can, userRoleKey).filter(tab => !tab.bottom).map(tab => (
                    <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}>
                      <i className={tab.icon} aria-hidden="true"></i>{language === 'es' ? tab.es : tab.en}<i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                    </button>
                  ))}
                </nav>
              )}
              <ClaimStatusHero companyLogo={companyLogo} companyName={companyName} />
            </div>
          ) : claimStatusContent}
        </AdjusterShell>
      ) : (
      <div className="dashboard-layout">
      {/* Menú de administración en 7 secciones (antes 9 pestañas sueltas que
          no entraban en pantallas angostas). Cada sección agrupa una o más
          vistas (activeTab); las que tienen varias muestran sub-pestañas. */}
      {/* En pantallas angostas la barra lateral se esconde y queda un botón
          fijo arriba (☰ + sección actual) que abre todas las secciones en un
          panel inferior. Antes era un <select> "Sección" que no se notaba. */}
      <div className="dashboard-mobile-menu">
        <button type="button" className="dashboard-mobile-menu-btn" aria-expanded={menuOpen} aria-controls="dashboard-admin-menu" onClick={() => setMenuOpen(open => !open)}>
          <i className="fa-solid fa-bars" aria-hidden="true"></i>
          <span className="dashboard-mobile-menu-text">
            <small>{language === 'es' ? 'Menú' : 'Menu'}</small>
            <strong>{currentSection ? (language === 'es' ? currentSection.es : currentSection.en) : ''}</strong>
          </span>
          {unreadActivity > 0 && <span className="debt-badge">{unreadActivity}<span className="sr-only"> {language === 'es' ? 'movimientos de actividad sin leer' : 'unread activity entries'}</span></span>}
          <i className={`fa-solid fa-chevron-${menuOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
        </button>
      </div>
      {menuOpen && <div className="dashboard-menu-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true"></div>}
      <nav id="dashboard-admin-menu" className={`dashboard-tabs ${menuOpen ? 'is-open' : ''}`} aria-label={language === 'es' ? 'Menú de administración' : 'Administration menu'}>
        {ADMIN_SECTIONS.map(section => {
          const isActive = section.tabs.includes(activeTab);
          const unread = section.id === 'requests' ? events.filter(item => !(item.readBy || []).includes(currentUser.uid)).length : 0;
          return (
            <button
              key={section.id}
              className={`tab-btn ${isActive ? 'active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={event => {
                // En pantallas angostas la barra se desplaza: que la sección
                // elegida no quede cortada en el borde.
                event.currentTarget.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
                setMenuOpen(false);
                if (!isActive) setActiveTab(firstTabOf(section));
              }}
            >
              <i className={section.icon} aria-hidden="true"></i>
              <span className="tab-label">{language === 'es' ? section.es : section.en}</span>
              {unread > 0 && (
                <span className="debt-badge" title={language === 'es' ? `${unread} movimientos de actividad sin leer` : `${unread} unread activity entries`}>
                  {unread}<span className="sr-only"> {language === 'es' ? 'movimientos de actividad sin leer' : 'unread activity entries'}</span>
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <main className="dashboard-content">
      {activeTab !== 'claimstatus' && <header className="workspace-page-heading"><h2>{(() => { const section = ADMIN_SECTIONS.find(sec => sec.tabs.includes(activeTab)); return section ? (language === 'es' ? section.es : section.en) : ''; })()}</h2></header>}
      {(() => {
        const section = ADMIN_SECTIONS.find(sec => sec.tabs.includes(activeTab));
        if (!section || section.tabs.length < 2) return null;
        return (
          <div className="cd-view-toggle dashboard-subtabs" role="group" aria-label={language === 'es' ? section.es : section.en}>
            {section.tabs.map(tab => (
              <button key={tab} type="button" className={`cd-view-btn ${activeTab === tab ? 'active' : ''}`} aria-pressed={activeTab === tab} onClick={() => setActiveTab(tab)}>
                {language === 'es' ? SUB_TAB_LABELS[tab][0] : SUB_TAB_LABELS[tab][1]}
                {tab === 'activity' && events.some(item => !(item.readBy || []).includes(currentUser.uid)) && (
                  <span className="debt-badge">{events.filter(item => !(item.readBy || []).includes(currentUser.uid)).length}</span>
                )}
              </button>
            ))}
          </div>
        );
      })()}
      {(activeTab === 'tickets' || activeTab === 'activity') && <TeamWorkspace activity={activeTab === 'activity'} tickets={tickets} events={events} claimNotes={claimNotes} user={currentUser} language={language} error={workspaceError} loading={workspaceLoading} onRetry={() => setWorkspaceAttempt(value => value + 1)} />}
      {activeTab === 'analytics' && (
        <>
          {/* Resumen en cards (solo admin; el ajustador no tiene esta pestaña).
              "CSR generado" es lo calculado a partir del cheque y los
              porcentajes; "Total pagado" es lo realmente depositado. */}
          <OverviewCards
            stats={stats}
            formatCurrency={formatCurrency}
            language={language}
            onNavigate={setActiveTab}
            schedule={<UpcomingMeetingsWidget variant="card" wide onOpenSchedule={() => setActiveTab('schedule')} />}
            chart={(
              <>
                {stats.undatedCSR !== 0 && <p role="status">{language === 'es' ? 'CSR sin fecha válida, excluido del gráfico: ' : 'CSR without a valid date, excluded from chart: '}{formatCurrency(stats.undatedCSR)}</p>}
                {stats.monthlyStats && stats.monthlyStats.length > 0 ? (
                  <Suspense fallback={tabFallback}>
                    <MonthlyCsrChart data={stats.monthlyStats} formatCurrency={formatCurrency} label={language === 'es' ? 'CSR generado' : 'CSR generated'} height={260} />
                  </Suspense>
                ) : (
                  <div className="no-data">
                    <p>{language === 'es' ? 'No hay datos de facturación mensuales' : 'No monthly billing data available'}</p>
                  </div>
                )}
              </>
            )}
          />

          {/* Desglose de ganancias basado en la recolección neta */}
          <div className="earnings-breakdown glass-card">
            <h3>
              <i className="fa-solid fa-chart-simple"></i>
              {language === 'es' ? 'Comisiones por Ajustador' : 'Commissions by Adjuster'}
            </h3>
            
            <div className="breakdown-summary">
              <div className="summary-item total">
                <span className="summary-label">{language === 'es' ? 'Comisión CSR Generada' : 'CSR Commission Generated'}</span>
                <span className="summary-value" style={{ color: 'var(--ui-success)' }}>{formatCurrency(stats.totalCSRCollected)}</span>
              </div>
            </div>

            {stats.adjusterStats && stats.adjusterStats.length > 0 && (
              <div className="adjuster-performance">
                <h4>{language === 'es' ? 'Ajustadores' : 'Adjusters'}</h4>
                <div className="table-responsive">
                  <table className="performance-table">
                    <thead>
                      <tr>
                        <th>{language === 'es' ? 'Ajustador' : 'Adjuster'}</th>
                        <th>{language === 'es' ? 'Reclamos' : 'Claims'}</th>
                        <th>{language === 'es' ? 'Monto Total de Cheques' : 'Total Checks'}</th>
                        <th>{language === 'es' ? 'Comisión del Ajustador' : 'Adjuster Commission'}</th>
                        <th>{language === 'es' ? 'Nuestro CSR Generado' : 'Our CSR Generated'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.adjusterStats.map((adjuster, idx) => (
                        <tr key={idx}>
                          <td><strong>{adjuster.name}</strong>{adjuster.company && <small> · {adjuster.company}</small>}</td>
                          <td>{adjuster.totalClaims}</td>
                          <td>{formatCurrency(adjuster.totalAmount)}</td>
                          <td style={{ color: 'var(--ui-warning)' }}>{formatCurrency(adjuster.adjusterCollected)}</td>
                          <td style={{ color: 'var(--ui-success)', fontWeight: 'bold' }}>
                            {formatCurrency(adjuster.csrCollected)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'create' && (
        <QuickCreate
          onClaimCreated={handleClaimCreated}
          duplicateFrom={duplicateSource}
          onDuplicateConsumed={() => setDuplicateSource(null)}
        />
      )}


      <Suspense fallback={tabFallback}>
      {activeTab === 'debts' && (
        <OperatorDebtsPanel
          debts={operatorDebts}
          onRefresh={refreshData}
          onDebtPaid={handleDebtPaid}
          onDebtDelete={handleDeleteDebt}
          fee={OPERATOR_FEE}
          onAuditDebts={auditOperatorDebts}
          onCleanupPremature={cleanupPrematureOperatorDebts}
          onUpdateFee={updatePendingDebtsToCurrentFee}
        />
      )}

      {activeTab === 'cuts' && isAdmin && <PaymentCutsPanel canOpenInvoices />}

      {activeTab === 'clientinvoices' && (
        <ClientInvoicesPanel
          invoices={clientInvoices}
          loading={clientInvoicesLoading}
          error={clientInvoicesError}
          companies={Object.keys(adjustersByCompany).sort()}
          adjustersByCompany={adjustersByCompany}
          fee={OPERATOR_FEE}
        />
      )}

      {activeTab === 'companies' && <Companies claims={claims} />}
      {activeTab === 'users' && isAdmin && <UsersManager />}
      {activeTab === 'statuses' && <StatusCatalog />}

      {activeTab === 'claimstatus' && claimStatusContent}
      {activeTab === 'commissions' && <ClaimsDirectory view="commissions" />}

      {activeTab === 'schedule' && <ClaimAgendaPage embedded />}

      {activeTab === 'oasync' && <OASync />}
      {activeTab === 'migration' && <FlowMigration />}
      {activeTab === 'csrenroll' && <CsrEnrollment />}
      </Suspense>
      </main>
      </div>
      )}
    </div>
  );
};

export default Dashboard;
